import { useDialogAwareHotkeys } from "@components/hotkeys/useDialogAwareHotkeys";
import { useCommandPaletteStore } from "@components/layout/commandPaletteStore";
import { Sidebar } from "@components/layout/Sidebar";
import { useFilteredTree } from "@components/search/useFilteredTree";
import { Create } from "@components/sidebar/Create";

import { TrashDialog } from "@components/trash/TrashDialog";
import { Tree } from "@components/tree/Tree";
import { TreeDndProvider } from "@components/tree/TreeDndProvider";
import { useTreeStore } from "@components/tree/treeStore";
import { useTreeData } from "@components/tree/useTreeData";
import { useTreeHistoryHotkeys } from "@components/tree/useTreeHistoryHotkeys";

import { createFileRoute, Outlet } from "@tanstack/react-router";
import { IconButton } from "@ui/icon-button";
import { Input } from "@ui/input";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@ui/resizable";
import { ScrollArea } from "@ui/scroll-area";
import { ChevronsDownUp, SearchIcon, Trash2Icon, XIcon } from "lucide-react";
import { useRef, useState } from "react";

export const Route = createFileRoute("/_data")({
  component: DataLayout,
});

function DataLayout() {
  return <DataContent />;
}

function DataContent() {
  const [trashOpen, setTrashOpen] = useState(false);
  const [treeFilter, setTreeFilter] = useState("");
  const [filterVisible, setFilterVisible] = useState(false);
  const filterInputRef = useRef<HTMLInputElement>(null);

  const setExpandedIds = useTreeStore((s) => s.setExpandedIds);
  const toggle = useCommandPaletteStore((s) => s.toggle);

  const { treeData } = useTreeData();
  const filtered = useFilteredTree(treeFilter, treeData);

  useTreeHistoryHotkeys();

  useDialogAwareHotkeys("/", (e) => {
    e.preventDefault();
    toggle();
  });

  function handleCollapseAll() {
    setExpandedIds(new Set());
  }

  function handleShowFilter() {
    setFilterVisible(true);
    requestAnimationFrame(() => filterInputRef.current?.focus());
  }

  function handleClearFilter() {
    setTreeFilter("");
    setFilterVisible(false);
  }

  function handleFilterKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Escape") {
      e.preventDefault();
      handleClearFilter();
    }
  }

  return (
    <TreeDndProvider>
      <div className="flex flex-1 overflow-hidden">
        <ResizablePanelGroup orientation="horizontal">
          <ResizablePanel defaultSize="288px" minSize="200px" maxSize="500px">
            <Sidebar>
              <div className="flex min-h-0 flex-1 flex-col">
                <div className="mb-4 flex h-8 shrink-0 items-center justify-between">
                  <span className="font-medium text-gray-600 text-sm">
                    Data
                  </span>
                  <div className="flex items-center gap-3">
                    <div className="flex items-center">
                      <IconButton
                        label="Filter tree"
                        onClick={handleShowFilter}
                      >
                        <SearchIcon />
                      </IconButton>
                      <IconButton
                        label="Collapse all"
                        onClick={handleCollapseAll}
                      >
                        <ChevronsDownUp />
                      </IconButton>
                      <IconButton
                        label="View trash"
                        onClick={() => setTrashOpen(true)}
                      >
                        <Trash2Icon />
                      </IconButton>
                    </div>
                    <Create />
                  </div>
                </div>
                {filterVisible && (
                  <div className="mb-2 flex items-center gap-1">
                    <Input
                      ref={filterInputRef}
                      placeholder="Filter files..."
                      value={treeFilter}
                      onChange={(e) => setTreeFilter(e.target.value)}
                      onKeyDown={handleFilterKeyDown}
                      className="h-7 text-xs"
                    />
                    <IconButton
                      label="Clear filter"
                      onClick={handleClearFilter}
                    >
                      <XIcon />
                    </IconButton>
                  </div>
                )}
                {/* Override Radix's inline display:table on the viewport
                    content div, which prevents text truncation in tree items. */}
                <div className="relative min-h-0 flex-1">
                  <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-3 bg-gradient-to-b from-stone-100 to-transparent" />
                  <ScrollArea className="[&_[data-slot=scroll-area-viewport]>div]:!block -mr-4 h-full pr-4">
                    <div className="pt-3">
                      <Tree
                        filteredItems={filtered ? filtered.items : undefined}
                      />
                    </div>
                  </ScrollArea>
                </div>
              </div>
            </Sidebar>
          </ResizablePanel>
          <ResizableHandle />
          <ResizablePanel>
            <div className="h-full overflow-auto">
              <Outlet />
            </div>
          </ResizablePanel>
        </ResizablePanelGroup>
        <TrashDialog open={trashOpen} onOpenChange={setTrashOpen} />
      </div>
    </TreeDndProvider>
  );
}
